import React, { Component, createRef } from 'react';
import { YMaps, Map } from 'react-yandex-maps';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import classNames from 'classnames/bind';

// Components
import Suggestions from '../Suggestions';
import InputField from '../../../InputField';
import SuggestionsPapajohns from '../SuggestionsPapajohns';
import { GMaps } from '../../../GoogleMap';

// State
import { mapLocale as mapLocaleSelector } from '../../../../state/modules/map/selectors';

// Config
import config from '../../../../config';

// Styles
import styles from './AddressesField.styl';

const cx = classNames.bind(styles);

class AddressesField extends Component {
  inputRef = createRef();

  resultContainerRef = null;

  get suggestCityArray() {
    const { userCity, list } = this.props;

    let cityArray = [];

    const { parent_id, name, id } = userCity;

    if (parent_id && parent_id > 0) {
      cityArray = list
        .filter(cityListItem => cityListItem.name !== name && cityListItem.parent_id === id)
        .map(filteredCity => ({
          city: filteredCity.name,
        }));
    }

    cityArray.push({ city: name });

    return cityArray;
  }

  get suggestKladrArray() {
    const { userCity } = this.props;

    let kladrArray = [];

    if (userCity) {
      kladrArray = (userCity || {}).suburb_kladr
        .split(';')
        .filter(_kladr => _kladr.trim().length > 0)
        .map(_kladr => ({
          kladr_id: _kladr.trim(),
        }));
    }

    return kladrArray;
  }

  setResultContainerRef = ref => {
    if (ref) {
      this.resultContainerRef = ref;
    }
  };

  // в обеих системах нам приходит объект item, у которого нужно взять значение value
  handleOnSelect = item => {
    const { input, onAddressSelect } = this.props;

    input.onChange(item?.value);
    onAddressSelect(item?.value);
  };

  // сделаем сброс поля, если мы стерли все символы. Больше он ни для чего не нужен
  handleOnBlur = (e, item) => {
    const { input, onAddressSelect, userCity } = this.props;

    if (
      (userCity.suggest_service === 'dadata' && !item?.value) ||
      (userCity.suggest_service !== 'dadata' && !e.target.value)
    ) {
      input.onChange('');
      onAddressSelect('');
    }
  };

  handleApiAvaliable = ymaps => {
    const { userCity } = this.props;

    const isMoscow =
      ['moscow', 'balashiha', 'zelenograd', 'korolev', 'mytishchi', 'shelkovo'].indexOf(
        userCity._supertag,
      ) !== -1;

    if (this.inputRef?.current) {
      ymaps.load(() => {
        const suggestView = new ymaps.SuggestView(this.inputRef.current, {
          provider: {
            suggest: request => {
              return ymaps.suggest(
                `${isMoscow ? '' : `${userCity.name}, `}${request}`,
                isMoscow && {
                  boundedBy: [
                    [56.062224, 36.610939],
                    [55.132283, 38.415443],
                  ],
                },
              );
            },
          },
          container: this.resultContainerRef,
        });

        suggestView.events.add('select', e => {
          this.handleOnSelect(e.get('item'));
        });
      });
    }
  };

  handleGoogleSuggest = () => {
    const { userCity } = this.props;

    if (this.inputRef?.current) {
      if (userCity.coordinates) {
        const lng = parseFloat(userCity.coordinates[0]);
        const lat = parseFloat(userCity.coordinates[1]);
        // Радиус в метрах: ~20 км — подсказки в рамках текущего города, без улиц из других городов
        const cityRadiusMeters = userCity.autocomplete_radius_meters ?? 20000;
        const circle = new google.maps.Circle({
          radius: cityRadiusMeters,
          center: { lng, lat },
        });
        const bounds = circle.getBounds();

        this.geocoder = new google.maps.Geocoder();
        this.cityBounds = bounds;

        this.suggService = new google.maps.places.Autocomplete(this.inputRef.current, {
          types: ['address'],
          fields: ['name'],
          componentRestrictions: { country: [config.legal] },
          bounds,
          strictBounds: true,
        });

        this.suggService.setBounds(bounds);
        this.suggService.addListener('place_changed', () =>
          this.handleOnSelect({ value: this.suggService.getPlace().name }),
        );
      }
    }
  };

  handleAddressKeyDown = e => {
    if (e.key !== 'Enter' || !this.geocoder || !this.cityBounds) return;
    const value = (this.inputRef?.current?.value || '').trim();
    if (!value) return;
    e.preventDefault();
    e.stopPropagation();
    this.geocoder.geocode(
      { address: value, bounds: this.cityBounds },
      (results, status) => {
        if (status !== google.maps.GeocoderStatus.OK || !results?.length) return;
        const inBounds = results.filter(r =>
          r.geometry?.location && this.cityBounds.contains(r.geometry.location),
        );
        const chosen = inBounds.length > 0 ? inBounds[0] : results[0];
        this.handleOnSelect({ value: chosen.formatted_address });
      },
    );
  };

  getSuggests = () => {
    const { mapLocale, input, newest, label, userCity } = this.props;

    if (config.legal === 'pl') {
      return (
        <GMaps
          loadingElement={<div />}
          containerElement={<div className={cx('AddressesField__search')} />}
          mapElement={<div />}
          defaultZoom={userCity.zoom}
          defaultCenter={userCity.coordinates}
          getMapRef={this.handleGoogleSuggest}
        >
          <div className={cx('AddressesField__resultContainer')} ref={this.setResultContainerRef} />
          <InputField
            {...input}
            className={cx('AddressesField__field')}
            autoComplete="off"
            _ref={this.inputRef}
            onChange={e => input.onChange(e.target.value)}
            onKeyDown={this.handleAddressKeyDown}
            onBlur={this.handleOnBlur}
            newest={newest}
            label={label}
          />
        </GMaps>
      );
    }

    switch (userCity.suggest_service) {
      case 'papajohns': {
        return (
          <div className={cx('AddressesField__search')}>
            <SuggestionsPapajohns
              input={input}
              onAddressSelect={this.handleOnSelect}
              onBlur={this.handleOnBlur}
              newest={newest}
              label={label}
            />
          </div>
        );
      }
      case 'dadata': {
        return (
          <div className={cx('AddressesField__search')}>
            <Suggestions
              {...input}
              id={input.name}
              autoComplete="off"
              locations={this.suggestCityArray.concat(this.suggestKladrArray)}
              delay={300}
              query={input.value}
              token={config.dadataToken}
              onAddressSelect={this.handleOnSelect}
              onBlur={this.handleOnBlur}
              label={label}
            />
          </div>
        );
      }
      case 'yandex':
      default: {
        return (
          <YMaps query={{ lang: mapLocale, apikey: config.yandexApiKey }} enterprise>
            <Map
              width="100%"
              height="100%"
              style={{ display: 'none' }}
              state={{ zoom: 10, center: [0, 0] }}
              onLoad={this.handleApiAvaliable}
              modules={['SuggestView', 'suggest']}
            />
            <div className={cx('AddressesField__search')}>
              <div
                className={cx('AddressesField__resultContainer')}
                ref={this.setResultContainerRef}
              />
              <InputField
                {...input}
                autoComplete="off"
                _ref={this.inputRef}
                onChange={e => input.onChange(e.target.value)}
                onBlur={this.handleOnBlur}
                newest={newest}
                label={label}
              />
            </div>
          </YMaps>
        );
      }
    }
  };

  render() {
    const { className } = this.props;

    return <div className={cx('AddressesField', className)}>{this.getSuggests()}</div>;
  }
}

const mapDispatchToProps = state => ({
  userCity: state.city.userCity,
  list: state.city.list,
  mapLocale: mapLocaleSelector(state),
});

export default connect(mapDispatchToProps)(AddressesField);

AddressesField.defaultProps = {
  className: '',
  newest: false,
};

AddressesField.propTypes = {
  className: PropTypes.string,
  label: PropTypes.string.isRequired,
  userCity: PropTypes.object.isRequired,
  list: PropTypes.array.isRequired,
  mapLocale: PropTypes.string.isRequired,
  onAddressSelect: PropTypes.func.isRequired,
  input: PropTypes.object.isRequired,
  newest: PropTypes.bool,
};
